import * as THREE from 'three';

import { Objects } from '../buildings/Objects';
import { levelOf } from './Movement';
import { axialToWorld } from './HexCoordinates';
import { HEX_SIZE, elevationToY } from './HexMetrics';
import { HexCellData, waterLevel } from './HexSurface';

/**
 * Zasobność pól, narysowana kołami — podgląd debugowy pod klawiszem 5.
 *
 * **Zastępuje rekwizyty, a nie dokłada się do nich.** Las to na ekranie kilka
 * drzew, a kamień kilka głazów, i po ich liczbie *da się* odczytać poziom —
 * tylko trzeba w tym celu policzyć drzewa na każdym polu z osobna. Ten widok
 * odpowiada na to samo pytanie jednym spojrzeniem: **czerwone jest ubogie,
 * zielone bogate**, a modele na czas oglądania znikają, żeby nie zasłaniały
 * odpowiedzi.
 *
 * Ryby dostały ten sam znacznik z konieczności, nie z wyboru: pod wodą nie stoi
 * nic, co dałoby się zobaczyć z góry, więc ławica **nie ma czego zastąpić** —
 * i dlatego widać ją wyłącznie tutaj. Wcześniej rysowała się zawsze, co było
 * wygodne przy jednym budynku i przestało być, gdy okazało się, że nad połową
 * mapy wisi pomarańczowa siatka.
 *
 * Które warstwy są złożami, mówi **słownik obiektów** — wpis naturalny niesie
 * swoją warstwę i próg. Nowe złoże jest tu więc wpisem w pliku, nie gałęzią.
 */

/** Największy promień koła, jako ułamek heksa. Poziom piąty. */
const WIDEST = 0.5;

/** Najmniejszy, poziom pierwszy. Poniżej tego koło ginie w pikselach terenu. */
const NARROWEST = 0.2;

/** Ile boków ma koło. Osiem wystarcza przy tej wielkości. */
const SIDES = 8;

/**
 * Ile nad gruntem leży znacznik.
 *
 * Koło jest płaskie, a teren pod nim bywa pochyły — bez podniesienia jego brzeg
 * schowałby się w zboczu. Nad wodą powód jest inny i mocniejszy: lustro
 * **faluje**, więc żaden offset głębi by tu nie wystarczył.
 */
const ABOVE = 0.06;

/** Najwyższy poziom warstwy zasobu, jak wszędzie indziej w tym kliencie. */
const MAX_LEVEL = 5;

export function createDepositMaterial(): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    // Kolor jedzie z bufora, bo każde koło ma swój: jeden materiał na chunk
    // i jedna siatka, zamiast pięciu materiałów po jednym na poziom.
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    side: THREE.FrontSide,
    // To nie jest rzecz w świecie, tylko rzecz o świecie: ma wyglądać tak samo
    // w dzień, w nocy i we mgle.
    fog: false,
  });
}

/**
 * Koła nad wszystkimi złożami chunka, w jednym buforze.
 *
 * `null`, gdy chunk nie ma żadnego — brak siatki jest tańszy od pustej.
 */
export function buildDepositGeometry(
  members: HexCellData[],
  objects: Objects
): THREE.BufferGeometry | null {
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  const tint = new THREE.Color();

  for (const cell of members) {
    const found = richestOn(cell, objects);
    if (!found) continue;

    const { x, z } = axialToWorld(cell.q, cell.r);

    // Złoże w wodzie leży pod lustrem, a znacznik ma leżeć **na** nim: pod
    // powierzchnią byłby zielonkawą plamą widzianą przez falującą wodę.
    const surface = found.inWater ? waterLevel(cell) : cell.elevation;
    if (surface === null) continue;
    const y = elevationToY(surface) + ABOVE;

    const share = (Math.min(found.level, MAX_LEVEL) - 1) / (MAX_LEVEL - 1);
    const radius = HEX_SIZE * (NARROWEST + (WIDEST - NARROWEST) * share);
    // Czerwień na jedynce, zieleń na piątce, po drodze przez żółć — to jest ten
    // sam gradient, po którym czyta się każdy pasek „mało/dużo", więc nie
    // trzeba do niego legendy.
    tint.setHSL(share / 3, 0.85, 0.5);

    const middle = positions.length / 3;
    positions.push(x, y, z);
    colors.push(tint.r, tint.g, tint.b);

    for (let side = 0; side < SIDES; side += 1) {
      const angle = (side / SIDES) * Math.PI * 2;
      positions.push(x + Math.cos(angle) * radius, y, z + Math.sin(angle) * radius);
      colors.push(tint.r, tint.g, tint.b);
    }

    for (let side = 0; side < SIDES; side += 1) {
      // Przeciwnie do wskazówek patrząc z góry, żeby przód trójkąta patrzył
      // w niebo — inaczej `FrontSide` schowałby całe koło.
      indices.push(middle, middle + 1 + ((side + 1) % SIDES), middle + 1 + side);
    }
  }

  if (indices.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  // Płaskie i poziome: liczenie normalnych dałoby dokładnie to samo, tylko
  // dłużej — a materiał i tak nie czyta światła.
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Najbogatsze złoże na polu, i czy leży ono w wodzie.
 *
 * **Jedno koło na pole, nie jedno na złoże.** Pole z lasem i kamieniem naraz
 * dostałoby dwa koła w tym samym miejscu, z których widać by było jedno —
 * i byłoby to gorsze niż jedno, bo nie wiadomo które. Pytanie, na które ten
 * widok odpowiada, brzmi „ile tu tego jest", więc odpowiada najwyższą liczbą.
 */
function richestOn(
  cell: HexCellData,
  objects: Objects
): { level: number; inWater: boolean } | null {
  let best: { level: number; inWater: boolean } | null = null;

  for (const info of Object.values(objects.objects)) {
    if (!info.from) continue;

    const level = levelFor(cell, info.from.layer);
    if (level < info.from.least) continue;
    if (!best || level > best.level) best = { level, inWater: !!info.from.inWater };
  }

  return best;
}

/**
 * Poziom warstwy na polu, na skali jeden do pięciu.
 *
 * Rzeka jest maską krawędzi, nie zasobnością, więc nie jest złożem i nie ma tu
 * czego pokazać — zwraca zero i wypada przez próg.
 */
function levelFor(cell: HexCellData, layer: string): number {
  if (layer === 'forest') return levelOf(cell.forest);
  if (layer === 'stone') return levelOf(cell.stone);
  if (layer === 'fish') return levelOf(cell.fish);
  return 0;
}
